// ===============================
// pages/Home.jsx
// FULLY DYNAMIC HOME PAGE
// ===============================

import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom"; // 🎯 ADDED useNavigate
import axios from "axios";
import { CartContext } from "../../context/CartContext";
import "./Home.css";
import rakhi from "../../assets/rakhi.jpeg";
import FestiveShowcase from "../Common/FestiveShowcase";
import janmashtamiBanner from "../../assets/janmashtami-banner.jpeg";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const PRODUCT_API = `${API_BASE_URL}/api/products`;
const CATEGORY_API = `${API_BASE_URL}/api/categories`;
const FALLBACK_IMAGE = "https://via.placeholder.com/400x400?text=No+Image";

const Home = () => {
  const { addToCart } = useContext(CartContext);
  const navigate = useNavigate(); // 🎯 Initialize navigate for button routing

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);

  useEffect(() => {
    fetchHomeData();
  }, []);

  const resolveImageUrl = (img) => {
    if (!img) return FALLBACK_IMAGE;

    if (img.startsWith("http://") || img.startsWith("https://") || img.startsWith("data:")) {
      return img;
    }

    let cleanPath = img;
    if (cleanPath.startsWith("/uploads/")) {
      cleanPath = cleanPath.replace("/uploads/", "");
    } else if (cleanPath.startsWith("uploads/")) {
      cleanPath = cleanPath.replace("uploads/", "");
    }

    const baseUrl = API_BASE_URL.endsWith("/") 
      ? API_BASE_URL.slice(0, -1) 
      : API_BASE_URL;

    return `${baseUrl}/uploads/${cleanPath}`;
  };

  const getProductImage = (product) => {
    if (product?.image) return resolveImageUrl(product.image);

    if (Array.isArray(product?.images) && product.images.length > 0) {
      return resolveImageUrl(product.images[0]);
    }

    return FALLBACK_IMAGE;
  };

  const getCategoryImage = (category) => {
    if (category?.image) return resolveImageUrl(category.image);
    return FALLBACK_IMAGE;
  };

  const fetchHomeData = async () => {
    try {
      setLoadingProducts(true);
      setLoadingCategories(true);

      const [productRes, categoryRes] = await Promise.all([
        axios.get(PRODUCT_API),
        axios.get(CATEGORY_API),
      ]);

      let allProducts = [];
      let allCategories = [];

      if (Array.isArray(productRes.data)) {
        allProducts = productRes.data;
      } else if (Array.isArray(productRes.data.products)) {
        allProducts = productRes.data.products;
      } else if (Array.isArray(productRes.data.data)) {
        allProducts = productRes.data.data;
      }

      if (Array.isArray(categoryRes.data)) {
        allCategories = categoryRes.data;
      } else if (Array.isArray(categoryRes.data.categories)) {
        allCategories = categoryRes.data.categories;
      } else if (Array.isArray(categoryRes.data.data)) {
        allCategories = categoryRes.data.data;
      }

      const activeProducts = allProducts.filter((item) => item.isActive !== false);

      setProducts(activeProducts.slice(0, 8));
      setCategories(allCategories.filter((item) => item.isActive !== false));
    } catch (error) {
      console.error("Home page fetch error:", error);
      setProducts([]);
      setCategories([]);
    } finally {
      setLoadingProducts(false);
      setLoadingCategories(false);
    }
  };

  return (
    <div className="home-page">
      {/* 1. HERO SECTION */}
      <section className="hero-section">
        <div className="container hero-content">
          <div className="hero-text">
            <span>The Versacore Edit ✨</span>
            <h1>Make it Personal, Make it Memorable ✨</h1>
            <p>
              From personalized gifts to customized fashion, discover beautifully 
              crafted pieces made to celebrate every occasion and make your moments 
              truly memorable.
            </p>
            <Link to="/shop">
              <button>Explore Collection</button>
            </Link>
          </div>

          <div className="hero-image">
            <img src={rakhi} alt="hero" />
          </div>
        </div>
      </section>

      <section className="categories-section">
        <div className="container">
          <div className="section-title">
            <h2>Shop By Categories</h2>
            <p>Explore trending collections</p>
          </div>

          {loadingCategories ? (
            <div className="loading-box">
              <h2>Loading Categories...</h2>
            </div>
          ) : categories.length === 0 ? (
            <div className="loading-box">
              <h2>No categories found.</h2>
            </div>
          ) : (
            <div className="categories-grid">
              {categories.map((item) => (
                <Link
                  to={`/shop?category=${item._id || item.id}`}
                  className="category-card"
                  key={item._id || item.id}
                >
                  <img src={getCategoryImage(item)} alt={item.name} />
                  <div className="category-overlay">
                    <h3>{item.name}</h3>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="products-section">
        <div className="container">
          <div className="section-title">
            <h2>Featured Products</h2>
            <p>Best selling products</p>
          </div>

          {loadingProducts ? (
            <div className="loading-box">
              <h2>Loading Products...</h2>
            </div>
          ) : products.length === 0 ? (
            <div className="loading-box">
              <h2>No products found.</h2>
            </div>
          ) : (
            <div className="products-grid">
              {products.map((product) => {
                // 🎯 CUSTOMIZATION ROUTING LOGIC
                // 🎯 NEW LOGIC: Checks if the SKU starts with "CUSTOM"
const isCustomizable = 
  (product?.sku && product.sku.toUpperCase().startsWith("CUSTOM")) || 
  (Array.isArray(product?.tags) && product.tags.includes("personalized"));
                const targetUrl = isCustomizable 
                  ? `/customize-cushion/${product._id}` 
                  : `/product/${product._id}`;

                return (
                  <div className="product-card" key={product._id}>
                    <Link to={targetUrl} className="product-link">
                      <div className="product-image" style={{ position: 'relative' }}>
                        <img src={getProductImage(product)} alt={product.name} />
                        
                        {/* 🎯 FLOATING BADGE FOR CUSTOM PRODUCTS */}
                        {isCustomizable && (
                          <span style={{ 
                            position: 'absolute', 
                            top: '10px', 
                            right: '10px', 
                            background: '#9c6b3d', 
                            color: '#fff', 
                            padding: '4px 10px', 
                            borderRadius: '20px', 
                            fontSize: '11px',
                            fontWeight: 'bold',
                            letterSpacing: '0.5px'
                          }}>
                            Personalize Me!
                          </span>
                        )}
                      </div>

                      <div className="product-info">
                        <span className="home-category">
                          {product.category?.name || product.category || "No Category"}
                        </span>
                        <h3>{product.name}</h3>
                        <p className="home-price">₹{product.price}</p>
                      </div>
                    </Link>

                    <div className="home-btn-area">
                      {/* 🎯 BUTTON ROUTING LOGIC */}
                      <button
                        onClick={() => {
                          if (isCustomizable) {
                            navigate(targetUrl); // Send to customizer
                          } else {
                            addToCart({ ...product, quantity: 1 }); // Standard add to cart
                          }
                        }}
                      >
                        {isCustomizable ? "Customize Now" : "Add To Cart"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="view-all-btn">
            <Link to="/shop">
              <button>View All Products</button>
            </Link>
          </div>
        </div>
      </section>

      <div>
        <FestiveShowcase 
          title="Raksha Bandhan Special ❤️"
          description="Celebrate the beautiful bond of love with personalised mugs, cushions, gift hampers, and thoughtful gifts made especially for your brother or sister."
          buttonText="Shop Rakhi Gifts"
          searchTag="rakhi"
        />
      </div>

      <section className="offer-banner">
        <div className="container offer-content">
          <div>
            <h2>Get 10% OFF on your Order</h2>
            <p>
              Use code{" "}
              <span
                style={{
                  background: "#ffffff",
                  color: "#111111",
                  padding: "4px 12px",
                  borderRadius: "6px",
                  fontWeight: "bold",
                  letterSpacing: "1.5px",
                  display: "inline-block",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  margin: "0 4px",
                }}
              >
                WELCOME
              </span>{" "}
              at checkout to claim your exclusive discount.
            </p>
          </div>
          <Link to="/shop">
            <button>Shop Now</button>
          </Link>
        </div>
      </section>

      <FestiveShowcase 
        title="Teacher's Day Specials 🎓"
        description="Express your gratitude to your mentors with custom printed mugs, personalized cushions, and executive gift sets designed for teachers."
        buttonText="Explore Teacher's Day Gifts"
        searchTag="teacher"
      />

      <section className="janmashtami-banner-section">
        <div className="container">
          <Link to="/shop?search=janmashtami" className="janmashtami-banner-wrapper">
            <img 
              src={janmashtamiBanner} 
              alt="Janmashtami Specials Coming Soon" 
              className="janmashtami-banner-img" 
            />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;